// 正式健診報告「顯示層」對照工具（Batch 6-B2.1）。
// 只做 presentation：把 Backend enum / 技術 key 轉成人類可理解的中文。
// 不重算 score / grade / coverage / priority，不解析公式，不重新判斷 category。

import type { CoverageLevel, ScoreMetricResult } from '@/types/gmb';

/** 評分資料覆蓋等級（僅 Frontend 顯示；Backend enum 不改）。 */
const COVERAGE_LEVEL_LABEL: Record<CoverageLevel, string> = {
  COMPLETE: '本次評分資料完整',
  GOOD: '本次評分資料良好',
  LIMITED: '本次評分資料有限',
  INSUFFICIENT: '本次評分資料不足',
};

export function formatCoverageLevelLabel(level: CoverageLevel): string {
  return COVERAGE_LEVEL_LABEL[level] ?? COVERAGE_LEVEL_LABEL.INSUFFICIENT;
}

/**
 * 同業分類來源 enum → 人類可理解中文。
 * 正式 UI 不得直接顯示 GOOGLE_PRIMARY_TYPE / GOOGLE_TYPES_FALLBACK /
 * USER_CONFIRMED / USER_CONFIRMATION_REQUIRED 等技術字串。
 */
const CATEGORY_SOURCE_LABEL: Record<string, string> = {
  GOOGLE_PRIMARY_TYPE: 'Google 明確類型',
  GOOGLE_TYPES_FALLBACK: 'Google 補充類型',
  USER_CONFIRMED: '已確認主要同業分類',
  USER_CONFIRMATION_REQUIRED: '尚待確認同業分類',
};

export function formatComparisonCategorySourceLabel(source?: string): string | null {
  if (!source) return null;
  return CATEGORY_SOURCE_LABEL[source] ?? null;
}

/** 用於 metric reason 內文中出現的 enum token（替換為人類可理解文字）。 */
const REASON_ENUM_LABEL: Record<string, string> = {
  GOOGLE_PRIMARY_TYPE: 'Google 明確類型',
  GOOGLE_TYPES_FALLBACK: 'Google 補充類型',
  USER_CONFIRMED: '已確認分類',
  USER_CONFIRMATION_REQUIRED: '尚待確認分類',
};

/**
 * 只針對已知 enum token 做文字替換（例如 M02 reason 內可能帶 (GOOGLE_PRIMARY_TYPE)）。
 * 不改分數、不改公式、不重新判斷 category。
 */
export function formatMetricReasonForDisplay(reason: string): string {
  if (!reason) return reason;
  let output = reason;
  for (const [token, label] of Object.entries(REASON_ENUM_LABEL)) {
    output = output.split(token).join(label);
  }
  return output;
}

/** 指標顯示名稱（Backend label 為優先；若缺失或誤帶技術 key，仍顯示人類可理解名稱）。 */
const METRIC_LABEL_BY_KEY: Record<string, string> = {
  M01: '營業狀態',
  M02: '主要商家類別',
  M03: '地址／服務區設定',
  M04: '聯絡電話',
  M05: '官方網站',
  M06: '營業時間',
  M07: '商家照片',
  M08: '評分絕對值',
  M09: '評論數絕對值',
  M10: '評分與同業比較',
  M11: '評論數與同業比較',
};

export function formatMetricDisplayLabel(key: string, label?: string): string {
  const trimmed = (label ?? '').trim();
  const looksTechnical = trimmed === '' || /^M\d{2}$/i.test(trimmed) || /^[a-z][a-z0-9_]*$/.test(trimmed);
  if (looksTechnical) return METRIC_LABEL_BY_KEY[key] ?? trimmed ?? key;
  return trimmed;
}

/**
 * 優先改善項目標題產品化（依 metricKey 對照；Backend title 僅作 fallback）。
 * 不改 Backend priority algorithm、不重新排序、不合併 priority。
 */
const PRIORITY_TITLE_BY_KEY: Record<string, string> = {
  M01: '確認 Google 商家營業狀態',
  M02: '優化主要商家類別',
  M03: '確認地址／服務區設定',
  M04: '補上公開聯絡電話',
  M05: '補強官方網站入口',
  M06: '補齊營業時間',
  M07: '補充代表性商家照片',
  M08: '改善 Google 星等表現',
  M09: '提升 Google 評論累積量',
  M10: '提升評分的同業競爭力',
  M11: '縮小與同業的評論量差距',
};

export function formatPriorityTitle(metricKey: string, fallbackTitle: string): string {
  return PRIORITY_TITLE_BY_KEY[metricKey] ?? fallbackTitle;
}

/**
 * UI-2B｜指標狀態中文化（主要 UI 不得顯示 SCORED / INSUFFICIENT_DATA 等技術 enum）。
 * SCORED 依完成率再細分為「表現良好 / 尚可 / 優先改善」，不改任何分數。
 */
export function metricStatusText(metric: ScoreMetricResult): string {
  if (metric.status === 'SCORED') {
    const ratio = metric.maxScore > 0 && typeof metric.score === 'number' ? metric.score / metric.maxScore : 0;
    if (ratio >= 0.8) return '表現良好';
    if (ratio >= 0.5) return '尚可';
    return '優先改善';
  }
  if (metric.status === 'INSUFFICIENT_PEER_DATA') return '同業資料不足';
  if (metric.status === 'NOT_APPLICABLE') return '不適用';
  return '資料不足';
}

/** 指標狀態語意色（good / warn / bad / na），供 UI 選擇樣式；未評分一律 na。 */
export function metricStatusTone(metric: ScoreMetricResult): 'good' | 'warn' | 'bad' | 'na' {
  if (metric.status !== 'SCORED') return 'na';
  const ratio = metric.maxScore > 0 && typeof metric.score === 'number' ? metric.score / metric.maxScore : 0;
  if (ratio >= 0.8) return 'good';
  if (ratio >= 0.5) return 'warn';
  return 'bad';
}